import { useState } from 'react';
import { useHistory } from '../../context/HistoryContext';
import { EmptyState, EmptyStateIcons } from '../common';
import { CalculationHistoryEntry } from '../../engine/types';
import './HistoryPanel.css';

interface HistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEntry?: (entry: CalculationHistoryEntry) => void;
}

export function HistoryPanel({ isOpen, onClose, onSelectEntry }: HistoryPanelProps) {
  const { entries, removeEntry, renameEntry, clearHistory } = useHistory();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getCalculatorLabel = (calculatorType: string) => {
    const labels: Record<string, string> = {
      ampacity: 'Wire Ampacity',
      conduit: 'Conduit Sizing',
      'voltage-drop': 'Voltage Drop',
      motor: 'Motor Calc',
      feeder: 'Feeder/Service Sizing',
      'multi-motor-feeder': 'Multi-Motor Feeder',
      'panel-schedule': 'Panel Schedule',
      transformer: 'Transformer',
      generator: 'Generator',
      'load-analysis': 'Load Analysis',
      'plc-io': 'PLC I/O List',
      'hazardous-locations': 'Hazardous Locations',
    };
    return labels[calculatorType] || calculatorType;
  };

  const startEditing = (entry: CalculationHistoryEntry) => {
    setEditingId(entry.id);
    setEditName(entry.name || '');
  };

  const saveEdit = () => {
    if (editingId) {
      renameEntry(editingId, editName);
      setEditingId(null);
      setEditName('');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="history-overlay" onClick={onClose} />
      <div className="history-panel">
        <div className="history-header">
          <h2>Calculation History</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {entries.length === 0 ? (
          <EmptyState
            icon={EmptyStateIcons.history}
            title="No calculation history"
            description="Your saved calculations will appear here. Use the calculators to generate results."
          />
        ) : (
          <>
            <div className="history-actions">
              <button className="clear-btn" onClick={clearHistory}>
                Clear All
              </button>
            </div>
            <div className="history-list">
              {entries.map(entry => (
                <div
                  key={entry.id}
                  className={`history-entry ${editingId === entry.id ? 'editing' : ''}`}
                  onClick={() => editingId !== entry.id && onSelectEntry?.(entry)}
                >
                  {editingId === entry.id ? (
                    <div className="entry-edit">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Enter a name..."
                        autoFocus
                        className="entry-name-input"
                      />
                      <div className="entry-edit-actions">
                        <button className="btn-save" onClick={saveEdit}>Save</button>
                        <button className="btn-cancel" onClick={cancelEdit}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="entry-header">
                        <span className="entry-calculator">{getCalculatorLabel(entry.calculatorType)}</span>
                        <span className="entry-time">{formatTimestamp(entry.timestamp)}</span>
                      </div>
                      {entry.name && <div className="entry-name">{entry.name}</div>}
                      <div className="entry-summary">{entry.summary}</div>
                      <div className="entry-actions">
                        <button
                          className="entry-rename"
                          onClick={(e) => { e.stopPropagation(); startEditing(entry); }}
                          aria-label="Rename entry"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                          </svg>
                        </button>
                        <button
                          className="entry-delete"
                          onClick={(e) => { e.stopPropagation(); removeEntry(entry.id); }}
                          aria-label="Delete entry"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
